import gsap from 'gsap';
import { Container, FillGradient, Graphics, Sprite } from 'pixi.js';
import { RealSpineAdapter } from '../animation/RealSpineAdapter.js';
import { createWinFrameSpineInstance } from '../rendering/spineLoader.js';
import { acquireSymbolSpine, releaseSymbolSpine } from '../rendering/symbolSpinePool.js';
import { createFireWinAnimatedSprite } from '../rendering/fireSpriteLoader.js';
import { gameConfig } from '../config/gameConfig.js';
import { spawnParticleBurst } from '../effects/particleBurst.js';
import { attachLuxuryGoldFrameFx } from '../effects/luxuryGoldFx.js';
import { getWildMultiplierTexture } from '../rendering/wildMultiplierBadge.js';

export class Symbol {
  /**
   * @param {{
   *   spineAssets: Record<number, any>,
   *   animationController: import('../animation/AnimationController.js').AnimationController,
   * }} opts
   */
  constructor(opts) {
    this.spineAssets = opts.spineAssets;
    this.animationController = opts.animationController;

    this.root = new Container();
    this._symbolId = 0;
    this._spine = null;
    this._adapter = null;
    /** @type {import('@esotericsoftware/spine-pixi-v8').Spine | null} */
    this._winFrameSpine = null;
    /** @type {import('pixi.js').AnimatedSprite | null} */
    this._fireSprite = null;
    /** @type {Container | null} */
    this._fireClipLayer = null;
    /** @type {Graphics | null} */
    this._fireMask = null;
    /** @type {Container | null} */
    this._spineClipLayer = null;
    /** @type {Graphics | null} */
    this._spineMask = null;
    /** @type {Graphics | null} */
    this._winLineBorder = null;
    /** @type {Graphics | null} */
    this._winLineBg = null;
    /** @type {import('gsap').Tween | null} */
    this._winLineBgTween = null;
    /** @type {{ stop: () => void, overlayRoot?: import('pixi.js').Container } | null} */
    this._luxuryGoldFx = null;
    this._fireFramesUnavailable = false;
    this._isSpinning = false;
    this._isActive = false;
    this._renderedSymbolId = null;
    /** True after {@link #playWin} until dim / normal / reset / spin. */
    this._winClipPlaying = false;
    /** Payline-cycle grid state — avoids stop→idle flash when re-applying the same presentation. */
    this._gridPresentation = 'normal';
    /** @type {import('gsap').Tween | null} */
    this._landPopTween = null;
    /** @type {Graphics | null} */
    this._scatterTeaseGlow = null;
    /** @type {import('gsap').Tween | null} */
    this._scatterTeaseGlowTween = null;
    /** @type {Sprite | null} */
    this._wildMultiplierSprite = null;
    this._wildMultiplier = 0;
  }

  _releaseSpineToPool() {
    if (!this._spine) return;
    const symbolId = this._renderedSymbolId;
    this._spineClipLayer?.removeChild(this._spine);
    if (Number.isFinite(symbolId) && symbolId > 0) {
      releaseSymbolSpine(symbolId, this._spine);
    } else {
      this._spine.destroy();
    }
    this._spine = null;
    this._renderedSymbolId = null;
    this._adapter = null;
  }

  /**
   * @param {number} symbolId
   * @param {{ skipIdle?: boolean }} [opts]
   */
  _createSpineForSymbol(symbolId, opts = {}) {
    if (!Number.isFinite(symbolId) || symbolId <= 0) {
      this._renderedSymbolId = symbolId;
      this._adapter = null;
      return;
    }

    if (this._spine) {
      this._releaseSpineToPool();
    }

    const spine = acquireSymbolSpine(symbolId, this.spineAssets);
    if (spine) {
      this._ensureSpineClipLayer();
      this._spine = spine;
      this._renderedSymbolId = symbolId;
      this._spineClipLayer.addChild(this._spine);
      this._attachSpineMask();
      this._sendWinFrameToBack();

      this._adapter = new RealSpineAdapter(this.root, spine, {
        animationController: this.animationController,
      });
      this._isSpinning = false;
      // Caller may immediately apply spinning/blur — skip idle to avoid a double Spine reset.
      if (!opts.skipIdle) {
        this._adapter.setIdle();
      }
    } else {
      console.warn(`Failed to create spine for symbol ${symbolId}`);
    }
  }

  _cellClipBounds() {
    const { cellWidth: cw, cellHeight: ch } = gameConfig;
    const u = gameConfig.ui;
    const inset = u.winLineCellBorderInset ?? 6;
    const r = u.winLineCellBorderRadius ?? 10;
    return {
      x: -cw / 2 + inset,
      y: -ch / 2 + inset,
      w: cw - inset * 2,
      h: ch - inset * 2,
      r,
    };
  }

  _paintCellClipMask(g) {
    const { x, y, w, h, r } = this._cellClipBounds();
    g.clear();
    g.roundRect(x, y, w, h, r).fill(0xffffff);
  }

  _paintFireMask() {
    if (!this._fireMask) return;
    this._paintCellClipMask(this._fireMask);
  }

  _paintSpineMask() {
    if (!this._spineMask) return;
    this._paintCellClipMask(this._spineMask);
  }

  _ensureSpineClipLayer() {
    if (this._spineClipLayer) return;
    this._spineMask = new Graphics();
    this._spineMask.eventMode = 'none';
    this._spineClipLayer = new Container();
    this._spineClipLayer.eventMode = 'none';
    this.root.addChild(this._spineClipLayer);
  }

  _attachSpineMask() {
    if (!this._spineMask || !this._spineClipLayer) return;
    if (this._spineMask.parent !== this.root) {
      this.root.addChildAt(this._spineMask, 0);
    }
    this._paintSpineMask();
    this._spineClipLayer.mask = null;
    this._spineClipLayer.mask = this._spineMask;
  }

  _sendWinFrameToBack() {
    let idx = 0;
    if (this._winLineBg?.parent === this.root) {
      this.root.addChildAt(this._winLineBg, idx++);
    }
    if (this._fireMask?.parent === this.root) {
      this.root.addChildAt(this._fireMask, idx++);
    }
    if (this._spineMask?.parent === this.root) {
      this.root.addChildAt(this._spineMask, idx++);
    }
    if (this._fireClipLayer?.parent === this.root) {
      this.root.addChildAt(this._fireClipLayer, idx++);
    }
    if (this._spineClipLayer?.parent === this.root) {
      this.root.addChildAt(this._spineClipLayer, idx++);
    }
    if (this._winFrameSpine?.parent === this.root) {
      this.root.addChildAt(this._winFrameSpine, idx);
    }
    if (this._winLineBorder?.parent === this.root) {
      this.root.addChild(this._winLineBorder);
    }
    this._bringWildMultiplierBadgeToFront();
  }

  _attachFireMask() {
    if (!this._fireMask || this._fireMask.parent === this.root) return;
    this.root.addChildAt(this._fireMask, 0);
    this._paintFireMask();
  }

  _detachFireMask() {
    if (!this._fireMask?.parent) return;
    this._fireMask.parent.removeChild(this._fireMask);
  }

  _ensureFireSprite() {
    if (this._fireClipLayer || this._fireFramesUnavailable) return;
    const frames = this.spineAssets?.fireWinFrames;
    if (!Array.isArray(frames) || !frames.length) {
      this._fireFramesUnavailable = true;
      return;
    }
    const sprite = createFireWinAnimatedSprite(frames);
    if (!sprite) {
      this._fireFramesUnavailable = true;
      return;
    }

    this._fireMask = new Graphics();
    this._fireMask.eventMode = 'none';

    this._fireClipLayer = new Container();
    this._fireClipLayer.eventMode = 'none';
    this._fireClipLayer.visible = false;
    this._fireClipLayer.addChild(sprite);
    this._fireSprite = sprite;

    this.root.addChildAt(this._fireClipLayer, 0);
  }

  _showFireSprite() {
    this._ensureFireSprite();
    if (!this._fireClipLayer || !this._fireSprite || !this._fireMask) return;
    this._attachFireMask();
    this._fireClipLayer.mask = null;
    this._fireClipLayer.mask = this._fireMask;
    this._sendWinFrameToBack();
    this._fireClipLayer.visible = true;
    this._fireSprite.visible = true;
    this._fireSprite.gotoAndPlay(0);
    const fx = gameConfig.visualFx ?? {};
    const fireAlpha = gameConfig.adapter.winFireAlpha ?? 0.9;
    const swing = fx.winFireAlphaSwing ?? 0.22;
    gsap.killTweensOf(this._fireSprite);
    this._fireSprite.alpha = fireAlpha;
    gsap.to(this._fireSprite, {
      alpha: Math.min(1, fireAlpha + swing),
      duration: gameConfig.adapter.winLineBgPulseSec ?? 1.35,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  _hideFireSprite() {
    if (!this._fireClipLayer || !this._fireSprite) return;
    gsap.killTweensOf(this._fireSprite);
    this._fireClipLayer.visible = false;
    this._fireClipLayer.mask = null;
    this._fireSprite.visible = false;
    this._fireSprite.stop();
    this._detachFireMask();
  }

  _ensureWinLineBorder() {
    if (this._winLineBorder) return;
    this._winLineBorder = new Graphics();
    this._winLineBorder.eventMode = 'none';
    this._winLineBorder.visible = false;
    this.root.addChild(this._winLineBorder);
  }

  _ensureWinLineBg() {
    if (this._winLineBg) return;
    this._winLineBg = new Graphics();
    this._winLineBg.eventMode = 'none';
    this._winLineBg.visible = false;
    this.root.addChildAt(this._winLineBg, 0);
  }

  _stopWinLineBgPulse() {
    if (this._winLineBgTween) {
      this._winLineBgTween.kill();
      this._winLineBgTween = null;
    }
  }

  _stopLuxuryGoldFx() {
    if (!this._luxuryGoldFx) return;
    this._luxuryGoldFx.stop();
    this._luxuryGoldFx = null;
  }

  /**
   * @param {boolean} strong
   */
  _syncLuxuryGoldFx(strong) {
    this._stopLuxuryGoldFx();
    if (!strong) return;

    const bounds = this._cellClipBounds();
    this._luxuryGoldFx = attachLuxuryGoldFrameFx(this.root, bounds, this.animationController, {
      loop: true,
    });
    const overlay = this._luxuryGoldFx.overlayRoot;
    if (overlay?.parent === this.root && this._winLineBorder) {
      const borderIdx = this.root.getChildIndex(this._winLineBorder);
      this.root.setChildIndex(overlay, borderIdx);
    }
  }

  /**
   * @param {number} color
   * @param {{ strong?: boolean }} [opts]
   */
  _paintWinLineBg(color, opts = {}) {
    this._ensureWinLineBg();
    const strong = opts.strong !== false;
    const { x, y, w, h, r } = this._cellClipBounds();
    const u = gameConfig.ui;
    const baseAlpha = strong
      ? (u.winLineCellBgAlpha ?? 0.32)
      : (u.winLineCellBgDimAlpha ?? 0.16);
    const grad = new FillGradient({
      type: 'radial',
      center: { x: 0.5, y: 0.45 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.72,
      textureSpace: 'local',
      colorStops: [
        { offset: 0, color },
        { offset: 0.55, color },
        { offset: 1, color: gameConfig.theme.panelDeep ?? 0x0a0604 },
      ],
    });

    const g = this._winLineBg;
    g.clear();
    // Release the previous paint's gradient texture; clear() alone does not free it.
    this._winLineBgGrad?.destroy?.();
    this._winLineBgGrad = grad;
    g.roundRect(x - 3, y - 3, w + 6, h + 6, r + 3);
    g.fill({ color, alpha: strong ? 0.14 : 0.08 });
    g.roundRect(x, y, w, h, r).fill(grad);
    g.alpha = baseAlpha;
    g.visible = true;
    this._sendWinFrameToBack();
    this._startWinLineBgPulse(strong, baseAlpha);
  }

  /** @param {boolean} strong @param {number} baseAlpha */
  _startWinLineBgPulse(strong, baseAlpha) {
    this._stopWinLineBgPulse();
    if (!this._winLineBg?.visible) return;
    const swing = gameConfig.ui.winLineCellBgPulseSwing ?? 0.14;
    const peak = Math.min(1, baseAlpha + (strong ? swing : swing * 0.65));
    this._winLineBg.alpha = baseAlpha;
    this._winLineBgTween = gsap.to(this._winLineBg, {
      alpha: peak,
      duration: gameConfig.adapter.winLineBgPulseSec ?? 1.35,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  _clearWinLineBg() {
    this._stopWinLineBgPulse();
    if (!this._winLineBg) return;
    this._winLineBg.visible = false;
    this._winLineBg.clear();
    this._winLineBg.alpha = 1;
    this._winLineBgGrad?.destroy?.();
    this._winLineBgGrad = null;
  }

  /**
   * @param {number} color
   * @param {{ strong?: boolean }} [opts]
   */
  showWinLineBorder(color, opts = {}) {
    this._ensureWinLineBorder();
    const strong = opts.strong !== false;
    this._paintWinLineBg(color, { strong });
    const { x, y, w, h, r } = this._cellClipBounds();
    const u = gameConfig.ui;
    const strokeW = strong
      ? (u.winLineCellBorderWidth ?? 3.5)
      : (u.winLineCellBorderDimWidth ?? 2.5);
    const alpha = strong ? 1 : (u.winLineCellBorderDimAlpha ?? 1);
    const glowAlpha = u.winLineCellBorderGlowAlpha ?? 0.82;

    const g = this._winLineBorder;
    g.clear();
    g.roundRect(x, y, w, h, r);
    g.stroke({
      width: strokeW + (strong ? 6 : 4),
      color,
      alpha: strong ? glowAlpha : glowAlpha * 0.65,
    });
    g.roundRect(x, y, w, h, r);
    g.stroke({ width: strokeW, color, alpha });
    g.visible = true;
    this.root.addChild(g);
    this._syncLuxuryGoldFx(strong);
    this._bringWildMultiplierBadgeToFront();
  }

  clearWinLineBorder() {
    this._stopLuxuryGoldFx();
    this._clearWinLineBg();
    if (!this._winLineBorder) return;
    this._winLineBorder.visible = false;
    this._winLineBorder.clear();
  }

  _ensureWinFrameSpine() {
    if (this._winFrameSpine || this._winFrameUnavailable) return;
    const spine = createWinFrameSpineInstance(this.spineAssets);
    if (!spine) {
      this._winFrameUnavailable = true;
      return;
    }
    this._winFrameSpine = spine;
    this.root.addChild(spine);
  }

  _hideWinFrameSpine() {
    if (!this._winFrameSpine) return;
    this._winFrameSpine.visible = false;
    try {
      this._winFrameSpine.state?.clearTracks();
    } catch {
      /* ignore */
    }
  }

  /**
   * @returns {string | null}
   */
  _resolveWinClipName() {
    const data = this._winFrameSpine?.skeleton?.data ?? this._spine?.skeleton?.data;
    if (!data) return null;
    const a = gameConfig.animations;
    if (a?.win && data.findAnimation(a.win)) return a.win;
    return data.findAnimation('win') ? 'win' : null;
  }

  /**
   * @param {import('@esotericsoftware/spine-pixi-v8').Spine} spine
   * @param {string} clipName
   * @param {boolean} [loop]
   */
  _setWinClipOnSpine(spine, clipName, loop = true) {
    if (!spine?.state) return;
    try {
      const entry = spine.state.setAnimation(0, clipName, loop);
      if (entry) entry.mixDuration = 0;
    } catch {
      /* ignore */
    }
  }

  _stopWinClipPlayback() {
    this._winClipPlaying = false;
    this._hideFireSprite();
    this._hideWinFrameSpine();
    if (this._adapter && typeof this._adapter.stop === 'function') {
      this._adapter.stop();
    }
    if (this._spine?.skeleton) {
      try {
        this._spine.state?.clearTracks();
        this._spine.skeleton.setToSetupPose();
      } catch {
        /* ignore */
      }
    }
  }

  /** Stop win frame/clips and full opacity — safe on every strip cell (cheap). */
  clearWinArtifacts() {
    this._stopWinClipPlayback();
    this.clearWinLineBorder();
    this._gridPresentation = 'normal';
    this.root.alpha = 1;
    this._clearScatterTeaseGlow();
  }

  _clearScatterTeaseGlow() {
    if (this._scatterTeaseGlowTween) {
      this._scatterTeaseGlowTween.kill();
      this._scatterTeaseGlowTween = null;
    }
    if (!this._scatterTeaseGlow) return;
    gsap.killTweensOf(this._scatterTeaseGlow);
    this._scatterTeaseGlow.parent?.removeChild(this._scatterTeaseGlow);
    this._scatterTeaseGlow.destroy();
    this._scatterTeaseGlow = null;
  }

  /**
   * Soft pulsing ring on scatter/coin cells during anticipation tease.
   * @param {boolean} on
   */
  setScatterTeaseGlow(on) {
    if (!on) {
      this._clearScatterTeaseGlow();
      return;
    }
    if (this._symbolId !== 10 && this._symbolId !== 11) {
      this._clearScatterTeaseGlow();
      return;
    }
    if (this._scatterTeaseGlow) return;
    const { x, y, w, h, r } = this._cellClipBounds();
    const g = new Graphics();
    g.roundRect(x - 4, y - 4, w + 8, h + 8, r + 4);
    g.stroke({ width: 3, color: gameConfig.theme?.gold ?? 0xe7c463, alpha: 0.75 });
    g.eventMode = 'none';
    this.root.addChildAt(g, 0);
    this._scatterTeaseGlow = g;
    this._scatterTeaseGlowTween = gsap.to(g, {
      alpha: 0.35,
      duration: 0.55,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  /**
   * Scale pop when wild / scatter / coin lands on a reel.
   * @param {import('pixi.js').Container} [burstParent]
   */
  playLandPop(burstParent = null) {
    const fx = gameConfig.visualFx ?? {};
    const ids = fx.landPopSymbolIds ?? [9, 10, 11];
    if (!ids.includes(this._symbolId)) return;

    if (this._landPopTween) {
      this._landPopTween.kill();
      this._landPopTween = null;
    }

    const peak = fx.landPopScalePeak ?? 1.18;
    const dur = fx.landPopDuration ?? 0.22;
    this.root.scale.set(peak, peak);
    this._landPopTween = gsap.to(this.root.scale, {
      x: 1,
      y: 1,
      duration: dur,
      ease: 'back.out(2.8)',
      onComplete: () => {
        this._landPopTween = null;
      },
    });

    const parent = burstParent ?? this.root.parent;
    if (parent) {
      spawnParticleBurst(parent, this.root.x, this.root.y, {
        count: 10,
        spread: 36,
        color: gameConfig.theme?.goldBright ?? 0xfff8dc,
      });
    }
  }

  /**
   * Start looping symbol + frame `win` clips until dim / spin / reset.
   */
  playWin() {
    if (!this._isActive || !this._adapter || this._winClipPlaying) return;

    const clipName = this._resolveWinClipName();
    if (!clipName) return;

    this._winClipPlaying = true;

    this._showFireSprite();

    this._ensureWinFrameSpine();
    if (this._winFrameSpine) {
      this._winFrameSpine.visible = true;
      this._sendWinFrameToBack();
      this._setWinClipOnSpine(this._winFrameSpine, clipName, true);
    }

    this._adapter.setWin();
  }

  /**
   * Play one full `win` from frame 0 (no loop). Used when cycling paylines so each line restarts cleanly.
   */
  playWinOnce() {
    if (!this._isActive || !this._adapter) return;

    const clipName = this._resolveWinClipName();
    if (!clipName) return;

    this._stopWinClipPlayback();

    this._winClipPlaying = true;

    this._showFireSprite();

    this._ensureWinFrameSpine();
    if (this._winFrameSpine) {
      this._winFrameSpine.visible = true;
      this._sendWinFrameToBack();
      this._setWinClipOnSpine(this._winFrameSpine, clipName, false);
    }

    if (typeof this._adapter.setWinOnce === 'function') {
      this._adapter.setWinOnce();
    } else {
      this._adapter.setWin();
    }
  }

  getAdapter() {
    return this._adapter;
  }

  /** @returns {number} Spine `win` clip length in seconds (0 if unavailable). */
  getWinClipDurationSec() {
    let max = 0;
    const a = this._adapter;
    if (a && typeof a.getClipDurationSec === 'function') {
      max = Math.max(max, a.getClipDurationSec(gameConfig.animations.win));
    }
    const clipName = this._resolveWinClipName();
    const frameData = this._winFrameSpine?.skeleton?.data ?? this._spine?.skeleton?.data;
    if (clipName && frameData) {
      const anim = frameData.findAnimation(clipName);
      if (anim?.duration > 0) max = Math.max(max, anim.duration);
    }
    return max;
  }

  /**
   * Small buy-bonus multiplier badge in the cell's top-right corner.
   * @param {number} multiplier 2, 3, 5, or 10
   */
  setWildMultiplierBadge(multiplier) {
    const n = Math.round(Number(multiplier));
    const tex = getWildMultiplierTexture(n);
    if (!tex || (n !== 2 && n !== 3 && n !== 5 && n !== 10)) {
      this.clearWildMultiplierBadge();
      return;
    }
    if (!this._wildMultiplierSprite) {
      const sprite = new Sprite(tex);
      sprite.eventMode = 'none';
      sprite.anchor.set(1, 0);
      this._wildMultiplierSprite = sprite;
      this.root.addChild(sprite);
    } else if (this._wildMultiplierSprite.texture !== tex) {
      this._wildMultiplierSprite.texture = tex;
    }
    const { cellWidth: cw, cellHeight: ch } = gameConfig;
    const size = Math.round(Math.min(cw, ch) * 0.3);
    const topInset = Math.max(16, Number(gameConfig.ui.reelViewportMaskTopExtraWhenIdle) || 0);
    const rightInset = 6;
    this._wildMultiplierSprite.width = size;
    this._wildMultiplierSprite.height = size;
    this._wildMultiplierSprite.position.set(cw / 2 - rightInset, -ch / 2 + topInset);
    this._wildMultiplierSprite.visible = true;
    this._wildMultiplier = n;
    this._bringWildMultiplierBadgeToFront();
  }

  clearWildMultiplierBadge() {
    this._wildMultiplier = 0;
    if (this._wildMultiplierSprite) this._wildMultiplierSprite.visible = false;
  }

  _bringWildMultiplierBadgeToFront() {
    const sprite = this._wildMultiplierSprite;
    if (!sprite?.visible || sprite.parent !== this.root) return;
    this.root.addChild(sprite);
  }

  /** @param {number} symbolId */
  setSymbolId(symbolId) {
    if (this._symbolId !== symbolId) this.clearWildMultiplierBadge();
    this._symbolId = symbolId;
    if (!this._isActive) return;
    if (this._renderedSymbolId === symbolId) return;
    this._createSpineForSymbol(symbolId);
  }

  getSymbolId() {
    return this._symbolId;
  }

  /**
   * @param {boolean} isSpinning
   */
  setSpinning(isSpinning) {
    if (!isSpinning) {
      this.clearSpinPresentation();
      return;
    }
    this._hideFireSprite();
    this.clearWinLineBorder();
    if (!this._isActive) return;
    if (this._isSpinning) return;
    this._isSpinning = true;
    this._winClipPlaying = false;
    this._hideWinFrameSpine();
    if (this._adapter) {
      if (typeof this._adapter.setSpinning === 'function') {
        this._adapter.setSpinning();
      } else {
        this._adapter.setBlur?.();
      }
    }
  }

  /** Drop reel blur/spin clip on this cell — always clears the flag AND the adapter, even when inactive. */
  clearSpinPresentation() {
    this._isSpinning = false;
    this._adapter?.setIdle?.();
  }

  /**
   * Grid presentation only — does not start Spine `win` clips (use {@link #playWin}).
   * Payline path cells that are not part of the win use idle; off-line cells use `noWin`.
   * Cleared in {@link #clearWinArtifacts} / {@link #prepareForNewRound} before the next spin.
   * @param {'normal' | 'dim' | 'lineIdle' | 'highlight'} state
   */
  setWinPresentation(state) {
    if (!this._isActive || !this._adapter) return;
    const dimAlpha = gameConfig.visualFx?.dimSymbolAlpha ?? 0.5;

    if (state === 'dim') {
      if (this._gridPresentation === 'dim') return;
      this._stopWinClipPlayback();
      this._gridPresentation = 'dim';
      this.root.alpha = dimAlpha;
      this._adapter.setNoWin();
      return;
    }

    if (state === 'lineIdle') {
      if (this._gridPresentation === 'lineIdle') return;
      this._stopWinClipPlayback();
      this._gridPresentation = 'lineIdle';
      this.root.alpha = dimAlpha;
      this._adapter.setIdle();
      return;
    }

    if (state === 'highlight') {
      this._gridPresentation = 'highlight';
      this.root.alpha = 1;
      return;
    }

    if (this._gridPresentation === 'normal') return;
    this._stopWinClipPlayback();
    this._gridPresentation = 'normal';
    this.root.alpha = 1;
    this._adapter.setIdle();
  }

  setHighlight(on) {
    this.setWinPresentation(on ? 'highlight' : 'normal');
  }

  /**
   * Hard reset before a new spin: idle adapters, no win/spin flags, neutral transform.
   * @param {boolean} [forceAdapter] When true, reset Spine even if the cell is off-screen inactive.
   */
  prepareForNewRound(forceAdapter = false) {
    this.clearWinArtifacts();
    this.clearWildMultiplierBadge();
    this._isSpinning = false;
    if (this._landPopTween) {
      this._landPopTween.kill();
      this._landPopTween = null;
    }
    this.root.scale.set(1, 1);
    this.root.rotation = 0;
    if (!forceAdapter && !this._isActive) return;
    if (!this._adapter) return;
    this._adapter.reset();
    this._adapter.setIdle();
  }

  resetVisual() {
    this.prepareForNewRound();
  }

  setActive(isActive, opts = {}) {
    if (this._isActive === isActive) return;
    this._isActive = isActive;
    this.root.visible = isActive;
    this.root.renderable = isActive;
    if (!isActive) {
      this._isSpinning = false;
      this._winClipPlaying = false;
      this._hideFireSprite();
      this._hideWinFrameSpine();
      this.clearWinLineBorder();
      this._adapter?.stop?.();
      this._releaseSpineToPool();
      return;
    }
    const skipIdle = !!opts.skipIdle;
    if (this._renderedSymbolId !== this._symbolId) {
      this._createSpineForSymbol(this._symbolId, { skipIdle });
    } else if (this._spine) {
      this._attachSpineMask();
      this._sendWinFrameToBack();
    }
    // When the reel will immediately apply spinning/blur, skip idle — avoids a wasted
    // clearTrack + setToSetupPose + idle, then another reset into blur (Android freeze source).
    if (skipIdle) return;
    // _createSpineForSymbol resets _isSpinning to false; never re-apply blur on activation.
    this._adapter?.setIdle?.();
  }

  isSpinning() {
    return this._isSpinning;
  }

  destroy() {
    if (this.root.destroyed) return;
    this._clearScatterTeaseGlow();
    this._stopLuxuryGoldFx();
    this._stopWinLineBgPulse();
    this._winLineBgGrad?.destroy?.();
    this._winLineBgGrad = null;
    this._hideFireSprite();
    if (this._landPopTween) {
      this._landPopTween.kill();
      this._landPopTween = null;
    }
    try {
      this._adapter?.stop?.();
    } catch {
      /* ignore */
    }
    this._releaseSpineToPool();
    this._winFrameSpine = null;
    this._fireSprite = null;
    this._fireClipLayer = null;
    this._fireMask = null;
    this._spineClipLayer = null;
    this._spineMask = null;
    this.root.destroy({ children: true });
  }
}
